import React, { useContext, useEffect } from "react"

import { DefaultModality } from "amazon-chime-sdk-js"
import Ding from "assets/ding.mp3"
import { useHistory } from "react-router-dom"

import { path } from "~Tutor/TutorRoutes"

import EventEmitter from "src/EventEmitter"
import classNames from "src/classNames"

import CloudroomContext from "components/ChimeCloudroom/CloudroomContext"
import LocalTile from "components/ChimeCloudroom/Shared/LocalTile"
import RemoteTile from "components/ChimeCloudroom/Shared/RemoteTile"
import SessionTimer from "components/SessionTimer"

import CaptionRail from "../Shared/CaptionRail"
import ErrorModal from "../Shared/ErrorModal"

import CloudroomCopilot from "./CloudroomCopilot"
import Controls from "./Controls"
import IncomingStudentsModal from "./IncomingStudentsModal"
import ScreenShareRequestedModal from "./ScreenShareRequestedModal"
import TutorSurveyModal from "./TutorSurveyModal"

const TutorLayout: React.FC = () => {
  const { session, sessionConfig, roster } = useContext(CloudroomContext)

  const history = useHistory()
  useEffect(() => {
    const onEndSession = () => {
      setTimeout(() => {
        history.push(path("sessionBill", { id: session.id }))
      }, 100)
    }
    EventEmitter.subscribe("end_session", onEndSession)
    return () => EventEmitter.unsubscribe("end_session", onEndSession)
  }, [history, session.id])

  useEffect(() => {
    const onAdminOrTeacherJoin = () => {
      new Audio(Ding).play()
    }
    EventEmitter.subscribe("admin_or_teacher_joined", onAdminOrTeacherJoin)
    return () => {
      EventEmitter.unsubscribe("admin_or_teacher_joined", onAdminOrTeacherJoin)
    }
  }, [])

  const remoteAttendeeIds = Object.keys(roster).filter(
    id =>
      new DefaultModality(id).base() !==
      session.chimeJoinInfo.attendee.attendeeId
  )

  const screenSharingAttendeeId = remoteAttendeeIds.find(id => {
    return roster[id].sharingContent
  })

  let gridClassNames
  const numAttendees = remoteAttendeeIds.length
  if (numAttendees < 2) gridClassNames = "flex items-center justify-center"
  else if (numAttendees < 3)
    gridClassNames = "sm:grid sm:grid-rows-2 sm:gap-x-4"
  else if (numAttendees < 5)
    gridClassNames = "sm:grid sm:grid-cols-2 sm:gap-x-4"
  else if (numAttendees < 10)
    gridClassNames = "sm:grid sm:grid-cols-3 sm:gap-x-3"
  else if (numAttendees < 16)
    gridClassNames = "sm:grid sm:grid-cols-4 sm:gap-x-2"

  return (
    <>
      <ErrorModal />
      <ScreenShareRequestedModal />
      <IncomingStudentsModal />
      {session.survey && <TutorSurveyModal />}
      {sessionConfig?.copilotEnabled && (
        <CloudroomCopilot sessionId={session.id} />
      )}

      <div className="grid h-screen grid-areas-tutor-slim sm:grid-areas-tutor-wide">
        <aside className="flex flex-col p-2 grid-in-aside sm:max-w-[300px] sm:justify-between">
          <div>
            <SessionTimer startsAt={session.startsAt} endsAt={session.endsAt} />
            <Controls />
          </div>
          <LocalTile />
        </aside>
        <main className="flex h-screen flex-col overflow-hidden">
          {screenSharingAttendeeId ? (
            <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
              <div className="mr-2 flex min-h-0 flex-1">
                <RemoteTile
                  attendeeId={screenSharingAttendeeId}
                  tutorView={true}
                />
              </div>
              <div className="flex max-h-screen max-w-[500px] flex-wrap overflow-scroll">
                {remoteAttendeeIds.map(id =>
                  id === screenSharingAttendeeId ? null : (
                    <RemoteTile
                      attendeeId={id}
                      key={id}
                      tutorView={true}
                      numAttendees={numAttendees}
                    />
                  )
                )}
              </div>
            </div>
          ) : (
            <div
              className={classNames(
                "min-h-0 flex-1 overflow-scroll",
                gridClassNames
              )}
            >
              {remoteAttendeeIds.map(id => (
                <RemoteTile
                  attendeeId={id}
                  key={id}
                  tutorView={true}
                  numAttendees={numAttendees}
                />
              ))}
            </div>
          )}
          {sessionConfig?.tutorCaptionsEnabled && <CaptionRail />}
        </main>
      </div>
    </>
  )
}

export default TutorLayout
